import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Brain, Cpu, DollarSign, TrendingUp, TrendingDown, AlertTriangle,
  Zap, Server, Activity, BarChart3, Sparkles, Eye, Target,
  ArrowUpRight, ArrowDownRight, Clock, Layers, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const AI_GPU_DEMO_DATA = {
  summary: {
    totalAISpend: 47832,
    monthOverMonth: 12.3,
    totalTokens: 284000000,
    avgCostPerToken: 0.0000168,
    gpuUtilization: 67.3,
    wastedGPUSpend: 8942,
    modelsActive: 14,
    inferenceRequests: 2840000
  },
  llmProviders: [
    { name: 'OpenAI', model: 'GPT-4o', tokens: 89000000, cost: 12450, trend: -3.2, icon: '🟢', costPerMToken: 5.0, requests: 890000 },
    { name: 'OpenAI', model: 'GPT-4 Turbo', tokens: 42000000, cost: 8400, trend: 8.5, icon: '🟢', costPerMToken: 10.0, requests: 420000 },
    { name: 'Anthropic', model: 'Claude Sonnet 4', tokens: 67000000, cost: 10050, trend: -1.8, icon: '🟣', costPerMToken: 3.0, requests: 670000 },
    { name: 'Anthropic', model: 'Claude Opus 4', tokens: 18000000, cost: 5400, trend: 22.1, icon: '🟣', costPerMToken: 15.0, requests: 180000 },
    { name: 'Google', model: 'Gemini 3 Pro', tokens: 45000000, cost: 4500, trend: -5.6, icon: '🔵', costPerMToken: 1.25, requests: 450000 },
    { name: 'Google', model: 'Gemini 3 Flash', tokens: 23000000, cost: 1150, trend: 41.2, icon: '🔵', costPerMToken: 0.075, requests: 230000 },
  ],
  gpuInstances: [
    { name: 'ml-training-cluster-1', type: 'p5.48xlarge', cloud: 'AWS', gpus: 8, utilization: 82, costPerHour: 98.32, monthlySpend: 14290, status: 'active' },
    { name: 'inference-prod-01', type: 'g5.12xlarge', cloud: 'AWS', gpus: 4, utilization: 91, costPerHour: 7.09, monthlySpend: 5105, status: 'active' },
    { name: 'inference-prod-02', type: 'Standard_NC24ads_A100', cloud: 'Azure', gpus: 1, utilization: 73, costPerHour: 3.67, monthlySpend: 2642, status: 'active' },
    { name: 'dev-experiment-gpu', type: 'a2-highgpu-4g', cloud: 'GCP', gpus: 4, utilization: 23, costPerHour: 14.69, monthlySpend: 4231, status: 'warning' },
    { name: 'batch-processing-01', type: 'p4d.24xlarge', cloud: 'AWS', gpus: 8, utilization: 45, costPerHour: 32.77, monthlySpend: 7864, status: 'warning' },
    { name: 'fine-tuning-staging', type: 'Standard_ND96asr', cloud: 'Azure', gpus: 8, utilization: 12, costPerHour: 27.20, monthlySpend: 3750, status: 'critical' },
  ],
  costByTeam: [
    { team: 'ML Platform', spend: 18420, tokens: 120000000, trend: 5.2 },
    { team: 'Product AI', spend: 14380, tokens: 89000000, trend: -2.1 },
    { team: 'Data Science', spend: 8930, tokens: 45000000, trend: 18.7 },
    { team: 'Customer AI', spend: 6102, tokens: 30000000, trend: -8.4 },
  ],
  alerts: [
    { severity: 'critical', message: 'fine-tuning-staging GPU at 12% utilization for 72h — $3,750/mo wasted', action: 'Terminate or resize', savings: 3300 },
    { severity: 'warning', message: 'GPT-4 Turbo usage up 22% this week — consider GPT-4o for eligible workloads', action: 'Switch model', savings: 2100 },
    { severity: 'warning', message: 'dev-experiment-gpu idle 77% of time — schedule auto-stop', action: 'Add schedule', savings: 3250 },
    { severity: 'info', message: 'Claude Sonnet 4 cost dropped 15% — validate quality for Opus tasks', action: 'A/B test', savings: 1800 },
  ],
  tokenTrend: [
    { date: 'Jan', openai: 45000, anthropic: 32000, google: 18000 },
    { date: 'Feb', openai: 52000, anthropic: 38000, google: 21000 },
    { date: 'Mar', openai: 48000, anthropic: 41000, google: 28000 },
    { date: 'Apr', openai: 61000, anthropic: 52000, google: 34000 },
    { date: 'May', openai: 58000, anthropic: 55000, google: 42000 },
    { date: 'Jun', openai: 63000, anthropic: 60000, google: 48000 },
  ]
};

const AIGPUCostIntelligence = () => {
  const { user } = useAuth();
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Iter84ak — fetch from real backend; fall back to local demo if API unavailable.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/finops/ai-gpu/intelligence`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (cancelled) return;
        const remote = r.data || {};
        // Backend returns a slimmer shape; merge over the rich local demo so the
        // UI stays full while the backend evolves to richer responses.
        const merged = remote.empty_state ? AI_GPU_DEMO_DATA : {
          ...AI_GPU_DEMO_DATA,
          summary: {
            ...AI_GPU_DEMO_DATA.summary,
            totalAISpend: remote.monthly_total_usd ?? AI_GPU_DEMO_DATA.summary.totalAISpend,
            monthOverMonth: remote.monthly_total_delta_pct ?? AI_GPU_DEMO_DATA.summary.monthOverMonth,
            wastedGPUSpend: remote.model_swap_savings_usd ?? AI_GPU_DEMO_DATA.summary.wastedGPUSpend,
          },
        };
        setData(merged);
      } catch (_e) {
        if (!cancelled) setData(AI_GPU_DEMO_DATA);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  if (!shouldShowData) {
    return <EmptyDataState title="AI/GPU Cost Intelligence" description="Connect your cloud accounts and LLM providers to track AI spending, GPU utilization, and token costs in real-time." />;
  }

  if (loading || !data) {
    return (
      <div className="p-6 space-y-4" data-testid="ai-gpu-loading">
        {[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 dark:bg-slate-800/50 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  const { summary, llmProviders, gpuInstances, costByTeam, alerts } = data;

  return (
    <div className="p-6 space-y-6" data-testid="ai-gpu-intelligence">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Brain className="w-7 h-7 text-cyan-400" />
            AI/GPU Cost Intelligence
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">Track every token, every GPU cycle, every dollar spent on AI</p>
        </div>
        <Button variant="outline" size="sm" className="border-slate-300 dark:border-slate-600" onClick={() => toast.success('Data refreshed')}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/40 dark:to-slate-900 border-emerald-200 dark:border-cyan-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">Total AI Spend</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">${summary.totalAISpend.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-1">
                  <ArrowUpRight className="w-3 h-3 text-red-400" />
                  <span className="text-xs text-red-400">+{summary.monthOverMonth}% MoM</span>
                </div>
              </div>
              <DollarSign className="w-8 h-8 text-cyan-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-cyan-50 bg-gradient-to-br from-cyan-50 to-white dark:from-cyan-900/40 dark:to-slate-900 border-emerald-200 dark:border-cyan-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">Total Tokens (MTD)</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{(summary.totalTokens / 1000000).toFixed(0)}M</p>
                <p className="text-xs text-slate-400 dark:text-slate-400">{summary.inferenceRequests.toLocaleString()} requests</p>
              </div>
              <Sparkles className="w-8 h-8 text-cyan-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/40 dark:to-slate-900 border-emerald-200 dark:border-emerald-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">GPU Utilization</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.gpuUtilization}%</p>
                <Progress value={summary.gpuUtilization} className="h-1.5 mt-2" />
              </div>
              <Cpu className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 bg-gradient-to-br from-red-50 to-white dark:from-red-900/40 dark:to-slate-900 border-red-200 dark:border-red-700/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 dark:text-slate-400">Wasted GPU Spend</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">${summary.wastedGPUSpend.toLocaleString()}</p>
                <p className="text-xs text-red-400">Recoverable this month</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Banner */}
      {alerts.filter(a => a.severity === 'critical').length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-semibold text-red-300">Critical AI Cost Alerts</span>
          </div>
          {alerts.filter(a => a.severity === 'critical').map((alert, i) => (
            <div key={i} className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-700 dark:text-slate-300">{alert.message}</span>
              <div className="flex items-center gap-2">
                <Badge variant="destructive" className="text-xs">${alert.savings.toLocaleString()} savings</Badge>
                <Button size="sm" variant="outline" className="text-xs h-7 border-red-600 text-red-300">{alert.action}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <TabsTrigger value="overview">LLM Providers</TabsTrigger>
          <TabsTrigger value="gpu">GPU Instances</TabsTrigger>
          <TabsTrigger value="teams">Cost by Team</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-cyan-400" />
                LLM API Cost Breakdown — Token-to-Dollar Mapping
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-7 text-xs text-slate-400 dark:text-slate-400 font-medium pb-2 border-b border-slate-200 dark:border-slate-700">
                  <span>Provider</span><span>Model</span><span className="text-right">Tokens (MTD)</span><span className="text-right">Cost</span><span className="text-right">$/1M Tokens</span><span className="text-right">Requests</span><span className="text-right">Trend</span>
                </div>
                {llmProviders.map((provider, i) => (
                  <div key={i} className="grid grid-cols-7 items-center py-2.5 border-b border-slate-200 dark:border-slate-200 dark:border-slate-800/50 hover:bg-slate-50 dark:bg-slate-800/30 rounded">
                    <span className="text-sm text-slate-900 dark:text-white">{provider.icon} {provider.name}</span>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-mono">{provider.model}</span>
                    <span className="text-sm text-right text-slate-700 dark:text-slate-300">{(provider.tokens / 1000000).toFixed(0)}M</span>
                    <span className="text-sm text-right text-slate-900 dark:text-white font-semibold">${provider.cost.toLocaleString()}</span>
                    <span className="text-sm text-right text-slate-400 dark:text-slate-400">${provider.costPerMToken}</span>
                    <span className="text-sm text-right text-slate-400 dark:text-slate-400">{(provider.requests / 1000).toFixed(0)}K</span>
                    <span className={`text-sm text-right font-medium ${provider.trend > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {provider.trend > 0 ? '+' : ''}{provider.trend}%
                    </span>
                  </div>
                ))}
                <div className="grid grid-cols-7 items-center pt-3 border-t border-slate-300 dark:border-slate-600 font-semibold">
                  <span className="text-sm text-slate-900 dark:text-white">Total</span>
                  <span></span>
                  <span className="text-sm text-right text-slate-900 dark:text-white">{(llmProviders.reduce((a,b) => a + b.tokens, 0) / 1000000).toFixed(0)}M</span>
                  <span className="text-sm text-right text-emerald-400">${llmProviders.reduce((a,b) => a + b.cost, 0).toLocaleString()}</span>
                  <span></span>
                  <span className="text-sm text-right text-slate-900 dark:text-white">{(llmProviders.reduce((a,b) => a + b.requests, 0) / 1000000).toFixed(1)}M</span>
                  <span></span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gpu" className="mt-4">
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-cyan-400" />
                GPU Instance Fleet — Utilization & Waste Detection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {gpuInstances.map((instance, i) => (
                  <div key={i} className={`p-4 rounded-lg border ${
                    instance.status === 'critical' ? 'border-red-200 dark:border-red-700/50 bg-red-900/10' :
                    instance.status === 'warning' ? 'border-yellow-200 dark:border-yellow-700/50 bg-yellow-900/10' :
                    'border-slate-200 dark:border-slate-700/50 bg-slate-800/30'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          instance.status === 'critical' ? 'bg-red-500 animate-pulse' :
                          instance.status === 'warning' ? 'bg-yellow-500' : 'bg-emerald-500'
                        }`} />
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{instance.name}</span>
                        <Badge variant="outline" className="text-xs">{instance.cloud}</Badge>
                        <span className="text-xs text-slate-400 dark:text-slate-400 font-mono">{instance.type}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-slate-400 dark:text-slate-400">{instance.gpus} GPUs</span>
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">${instance.monthlySpend.toLocaleString()}/mo</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 dark:text-slate-400 w-20">Utilization:</span>
                      <Progress value={instance.utilization} className="flex-1 h-2" />
                      <span className={`text-xs font-bold w-10 ${
                        instance.utilization < 30 ? 'text-red-400' :
                        instance.utilization < 60 ? 'text-yellow-400' : 'text-emerald-400'
                      }`}>{instance.utilization}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teams" className="mt-4">
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-white">AI Cost by Team</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {costByTeam.map((team, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700/50">
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{team.team}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-400">{(team.tokens / 1000000).toFixed(0)}M tokens</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">${team.spend.toLocaleString()}</p>
                      <p className={`text-xs ${team.trend > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {team.trend > 0 ? '+' : ''}{team.trend}% MoM
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="optimization" className="mt-4">
          <div className="space-y-4">
            <Card className="bg-slate-900 bg-gradient-to-r from-emerald-900/30 to-slate-900 border-emerald-200 dark:border-cyan-700/30">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">Total Recoverable AI Savings</h3>
                    <p className="text-slate-400 dark:text-slate-400 text-sm">Based on current utilization patterns and model routing</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-emerald-400">${alerts.reduce((a,b) => a + b.savings, 0).toLocaleString()}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-400">per month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {alerts.map((alert, i) => (
              <Card key={i} className={`border-l-4 ${
                alert.severity === 'critical' ? 'border-l-red-500 bg-red-900/10' :
                alert.severity === 'warning' ? 'border-l-yellow-500 bg-yellow-900/10' :
                'border-l-blue-500 bg-blue-900/10'
              } border-slate-200 dark:border-slate-700`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-xs ${
                        alert.severity === 'critical' ? 'bg-red-600' :
                        alert.severity === 'warning' ? 'bg-yellow-600' : 'bg-blue-600'
                      }`}>{alert.severity}</Badge>
                      <span className="text-xs text-emerald-400 font-semibold">Save ${alert.savings.toLocaleString()}/mo</span>
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{alert.message}</p>
                  </div>
                  <Button size="sm" className="ml-4 bg-cyan-600 hover:bg-cyan-700 text-xs">
                    {alert.action}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIGPUCostIntelligence;
